import { useTranslation } from 'react-i18next';
import { useModuleStrings } from '@platform/modularity/useModuleStrings';
import type { IRecordListModel } from '@platform/core/types';
import { useCreateKinds } from '../create/useCreateKinds';
import { accusativeUk, createLabel } from './createLabel';
import type { ICreateButton } from './ICreateButton';
import type { IListActions } from './IListActions';
import { ownCreateLabel } from './ownCreateLabel';

/**
 * Кнопка створення панелі списку (прототип v4): «+ Створити <тип>» — власний підпис типу з пакета або знахідний відмінок однини; тип із полем
 * виду (бізнес-аналіз) — меню «+ Створити ▾» з видами (варіанти поля), а коли записи списку мають один вид — «+ Створити <вид>» (рядки
 * `listbar.createKind.<вид>`: «проблему», «потребу», «шлях»), що створює запис цього виду.
 */
export function useCreateButton(model: IRecordListModel, actions: IListActions): ICreateButton {
  const { t, i18n } = useTranslation();
  const strings = useModuleStrings(model.module.module.key);
  const kinds = useCreateKinds(model);
  const rt = model.rt;
  if (!kinds) {
    return { text: createLabel(strings.entity(rt.key, 'singular', rt.singular ?? null), strings.lang, t, ownCreateLabel(strings.lang, model.module.module.key, rt)) };
  }
  const label = (value: string) => strings.option(rt.key, kinds.field, value, kinds.options.find((o) => o.value === value)?.label ?? value);
  if (kinds.single) {
    const key = `listbar.createKind.${kinds.single}`;
    const name = i18n.exists(key) ? t(key) : strings.lang === 'uk' ? accusativeUk(label(kinds.single)) : label(kinds.single).toLowerCase();
    return { text: t('listbar.create', { name }) };
  }
  const create = (value: string) => () => actions.createWith({ [kinds.field]: value });
  return { text: t('listbar.createAny'), kinds: kinds.options.map((o) => ({ key: o.value, label: label(o.value), onSelect: create(o.value) })) };
}
